<template>
  <main
    id="stock"
    class="page-container stock-list"
    v-if="$route.name === 'listini-category'"
  >
    <div class="table-container-stock">
      <!-- LOADER OVERLAY -->
      <div
        v-if="isLoading"
        class="loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="spinner" role="status"></div>
        <p>Carico gli articoli…</p>
      </div>

      <div class="items-toolbar">
        <button
          class="primary-btn"
          @click="openNewArticle"
          :disabled="isLoading"
        >
          <i class="fa-solid fa-plus"></i> Nuovo articolo
        </button>
        <div class="items-toolbar__right">
          <button
            class="refresh-btn"
            :class="{ refreshed }"
            @click="handleRefresh"
            :disabled="refreshing || isLoading"
            title="Aggiorna"
          >
            <i
              :class="[
                'fa-solid',
                refreshed ? 'fa-check' : 'fa-arrows-rotate',
                { 'fa-spin': refreshing },
              ]"
            ></i>
            <span>{{
              refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
            }}</span>
          </button>
        </div>
      </div>
      <div class="tbl-wrap" :class="{ dimmed: isLoading }">
        <table class="tbl item-table">
          <thead>
            <tr>
              <th v-for="col in columns" :key="col.key">{{ col.label }}</th>
              <th style="text-align: left">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(item, index) in items" :key="index">
              <tr class="item-row">
                <td
                  v-for="col in columns"
                  :key="col.key"
                  :class="{ 'td-name': col.truncate }"
                  :title="col.truncate ? item[col.key] ?? '' : undefined"
                >
                  <span v-if="col.format === 'price'"
                    >{{ formatPriceToPrint(item[col.key])
                    }}{{ priceUnit(item) }}</span
                  >
                  <span v-else-if="col.format === 'status'">
                    <span :class="`badge badge--${item[col.key]}`">{{
                      item[col.key]
                    }}</span>
                  </span>
                  <span v-else-if="col.format === 'dimensions'">
                    {{
                      item.widthCm && item.heightCm
                        ? `${item.widthCm}×${item.heightCm}`
                        : "—"
                    }}
                  </span>
                  <span v-else-if="col.format === 'scelta'">{{
                    item.specs?.scelta ?? "—"
                  }}</span>
                  <span v-else-if="col.format === 'decimal2'">{{
                    item[col.key] != null && item[col.key] !== ""
                      ? formatDecimal(item[col.key])
                      : "—"
                  }}</span>
                  <span v-else>{{ item[col.key] ?? "—" }}</span>
                </td>
                <td class="actions-cell">
                  <button
                    class="icons-button"
                    @click="goToEditItem(item.id)"
                    title="Modifica articolo"
                  >
                    <i class="fa-solid fa-pen-to-square"></i>
                  </button>
                  <button
                    class="icons-button icons-button--danger"
                    @click="openArchiveModal(item)"
                    title="Archivia articolo"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                  </button>
                </td>
              </tr>
            </template>
            <tr v-if="items.length === 0">
              <td :colspan="columns.length + 1" class="no-result">
                Nessun articolo trovato.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <div class="pagination-left">
          <div class="search-container">
            <i
              v-if="!searchTerm"
              class="fa-solid fa-magnifying-glass search-icon"
            ></i>
            <input
              type="text"
              v-model="searchTerm"
              @keyup.enter="onSearch"
              placeholder="Cerca per codice o nome..."
              class="search-box"
            />
            <button
              v-if="searchTerm"
              @click="onResetSearch"
              class="clear-search-button"
              :disabled="isLoading"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
        <div class="pagination-right">
          <div class="pagination-info">
            {{ pagination.startItem }}-{{ pagination.endItem }} di
            {{ pagination.totalItems }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="isLoading || pagination.currentPage === 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="isLoading || pagination.currentPage === 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: pagination.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.totalPages)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal archiviazione -->
    <VueFinalModal
      v-model="showArchiveModal"
      :clickToClose="!archiving"
      :escToClose="!archiving"
      classes="documents-modal"
      content-class="confirm-modal-content modal--archive"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="closeArchiveModal"
          :disabled="archiving"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Archivia articolo</h3>
        <p class="modal__subtitle">
          Archiviare
          <strong>{{ selectedItem?.itemName || selectedItem?.brand }}</strong
          >?
        </p>
        <div class="modal-actions">
          <button
            class="secondary-btn"
            @click="closeArchiveModal"
            :disabled="archiving"
          >
            Annulla
          </button>
          <button
            class="primary-btn confirm-btn"
            @click="confirmArchive"
            :disabled="archiving"
          >
            <span v-if="archiving">Archivia…</span>
            <span v-else>Archivia</span>
          </button>
        </div>
      </template>
    </VueFinalModal>
  </main>
  <router-view />
</template>

<script>
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import { formatPriceToPrint, formatDecimal } from "../utilities/formatting";

/**
 * Colonne della tabella listino.
 *
 * Esiste una sola scheda di listino, generica (`altro`), quindi esiste un solo
 * set di colonne. I set specifici per categoria merceologica — area, pezzo,
 * lineare, arredo, bagno, parquet — e le relative tassonomie di
 * sotto-categoria sono stati rimossi con le schede a cui appartenevano: non
 * erano più raggiungibili da nessun percorso dell'applicazione.
 *
 * Le funzioni di raggruppamento per categoria restano dove servono davvero
 * (magazzino, preventivi, creazione e modifica articolo), perché lì gli
 * articoli possono appartenere a qualunque categoria del modello.
 */
const GENERIC_COLUMNS = [
  { key: "displayCode", label: "Codice", truncate: true },
  { key: "brand", label: "Brand", truncate: true },
  { key: "itemName", label: "Nome", truncate: true },
  { key: "notes", label: "Descrizione", truncate: true },
  { key: "price", label: "Prezzo", format: "price" },
];

export default {
  name: "ListiniCategoryPage",
  props: {
    category: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      searchTerm: "",
      selectedItem: null,
      showArchiveModal: false,
      archiving: false,
      refreshing: false,
      refreshed: false,
    };
  },
  computed: {
    ...mapGetters({
      allItems: "items/list",
      pagination: "items/pagination",
    }),
    items() {
      // Codice visibile: supplierCode (quello del listino) se presente,
      // altrimenti itemCode. Il codice tecnico interno non viene mai mostrato.
      return this.allItems
        .filter((i) => i.category === this.category)
        .map((i) => ({ ...i, displayCode: i.supplierCode || i.itemCode }));
    },
    columns() {
      // Unica scheda, unico set di colonne.
      return GENERIC_COLUMNS;
    },
    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  methods: {
    ...mapActions(["fetchItems"]),
    formatPriceToPrint,
    formatDecimal,

    // Suffisso unità del prezzo, dedotto dai dati della riga: la scheda è
    // generica, quindi non c'è una categoria da cui inferirlo.
    priceUnit(item) {
      const name = String(item?.itemName || "").toLowerCase();
      const sub = String(item?.subCategory || "").toLowerCase();
      if (name.includes("battiscopa") || sub.includes("battiscopa"))
        return "/ml";
      if (Number(item?.sqmPerBox) > 0) return "/mq";
      if (Number(item?.mlPerBox) > 0) return "/ml";
      return "/pz";
    },

    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.loadItems(this.pagination.currentPage);
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    openNewArticle() {
      // Nessuna tipologia da scegliere: la scheda è generica.
      this.$router.push(`/home/listini/${this.category}/nuovo`);
    },

    openArchiveModal(item) {
      this.selectedItem = item;
      this.showArchiveModal = true;
    },
    closeArchiveModal() {
      if (this.archiving) return;
      this.showArchiveModal = false;
      this.selectedItem = null;
    },

    goToEditItem(itemId) {
      this.$router.push({
        name: "listini-edit-item",
        params: { category: this.category, itemId },
      });
    },

    async onSearch() {
      await this.loadItems(1);
    },
    async onResetSearch() {
      this.searchTerm = "";
      await this.loadItems(1);
    },
    goToPage(page) {
      if (this.isLoading) return;
      if (page < 1 || page > this.pagination.totalPages) return;
      this.loadItems(page);
    },
    async loadItems(page = 1) {
      this.isLoading = true;
      try {
        await this.fetchItems({
          page,
          limit: 8,
          category: this.category,
          search: this.searchTerm,
        });
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            "Errore durante il caricamento degli articoli."
        );
      } finally {
        this.isLoading = false;
      }
    },

    async confirmArchive() {
      if (!this.selectedItem) return;
      try {
        this.archiving = true;
        await this.$axios.post(
          `item/archive/${this.selectedItem.id}`,
          JSON.stringify({ archived: true })
        );
        this.$toast.success("Articolo archiviato con successo.");
        this.showArchiveModal = false;
        await this.loadItems(this.pagination.currentPage);
        if (
          this.pagination.currentPage > this.pagination.totalPages &&
          this.pagination.totalPages >= 1
        ) {
          await this.loadItems(this.pagination.totalPages);
        }
      } catch (err) {
        this.$toast.error(
          err.response?.data?.details || "Impossibile archiviare l'articolo."
        );
      } finally {
        this.archiving = false;
        this.selectedItem = null;
      }
    },
  },
  mounted() {
    if (this.category) this.loadItems(1);
  },
  watch: {
    category() {
      this.searchTerm = "";
      this.loadItems(1);
    },
    searchTerm() {
      this.onSearch();
    },
  },
};
</script>
<style src="@/css/StockPage.css" />
